// Central source-of-truth for detail-view DISPLAY defaults.
//
// These values used to be hard-coded inside components/detail/fileUtils.js and
// components/detail/FieldValue.jsx. They now live here, are seeded with the same
// built-in defaults, and are overridable from the admin page
// (Admin → Detail Groups → Display Defaults), which persists them via
// GET/POST /admin/detail-defaults.
//
// This is a plain module (not a React component / context) so fileUtils.js — a
// non-component helper — can read it synchronously. Consumers read getDefaults()
// at render time; loadDetailDefaults() fetches the saved values once and updates
// the store (falling back to the built-ins on any failure).

import { getDetailDefaults as fetchDetailDefaults } from './adminApi';

// Built-in defaults — exactly the previous hard-coded behaviour. S3 base still
// honours VITE_S3_BASE_URL as the env-level seed; the admin value overrides it.
export const BUILT_IN_DEFAULTS = Object.freeze({
  s3BaseUrl: (
    import.meta.env?.VITE_S3_BASE_URL ||
    'https://zinnext-devlopment-ap-south-1.s3.ap-south-1.amazonaws.com'
  ).replace(/\/+$/, ''),
  dateFormat: 'DD MMM YYYY',
  booleanTrueLabel: 'Yes',
  booleanFalseLabel: 'No',
  emptyPlaceholder: '-',
  separator: ' - ',
  documentNameOrder: ['name', 'documentName', 'uploadName', 'uploadedFileName', 'uniqueName'],
});

let current = { ...BUILT_IN_DEFAULTS };
let loadPromise = null;

// getDefaults returns the live defaults (built-ins until loadDetailDefaults runs).
export function getDefaults() {
  return current;
}

// sanitize keeps only the keys that carry a real value, so a partial/empty saved
// config never blanks out a built-in.
function sanitize(d) {
  if (!d || typeof d !== 'object') return {};
  const out = {};
  if (d.s3BaseUrl) out.s3BaseUrl = String(d.s3BaseUrl).replace(/\/+$/, '');
  if (d.dateFormat) out.dateFormat = d.dateFormat;
  if (d.booleanTrueLabel) out.booleanTrueLabel = d.booleanTrueLabel;
  if (d.booleanFalseLabel) out.booleanFalseLabel = d.booleanFalseLabel;
  if (d.emptyPlaceholder) out.emptyPlaceholder = d.emptyPlaceholder;
  if (typeof d.separator === 'string' && d.separator !== '') out.separator = d.separator;
  if (Array.isArray(d.documentNameOrder) && d.documentNameOrder.length) out.documentNameOrder = d.documentNameOrder;
  return out;
}

// applyDefaults merges a (partial) config over the current store immediately —
// used by the admin page right after a successful save so the change is live
// without a reload.
export function applyDefaults(partial) {
  current = { ...current, ...sanitize(partial) };
  return current;
}

// loadDetailDefaults fetches the saved defaults once (cached). Safe to call from
// anywhere — failures silently keep the built-ins. Pass force=true to refetch.
export function loadDetailDefaults(force = false) {
  if (loadPromise && !force) return loadPromise;
  loadPromise = fetchDetailDefaults()
    .then((d) => applyDefaults(d))
    .catch(() => current);
  return loadPromise;
}
